import { toJS } from "mobx";
import { useRootStore } from "../../../../stores/hooks";
import type {
  ActionCandidate,
  InputCandidate,
} from "../../../../types/candidate";
import { CredentialSelector } from "../../Selectors/CredentialSelector";
import * as A from "../../../../stores/automation/mergers/actions";
import type { AuthAttempt } from "../../../../auth";
import { authDispatcher } from "../../../../auth";
import { useEngineClient } from "../../../../services/engineClient";
import type {
  AuthenticationMethodDescription,
  InputDescription,
} from "@smartsuite/automation-engine-api";
import {
  Position,
  SelectEditorDescription_Type,
} from "@smartsuite/automation-engine-api";
import { StyledInputWrapper } from "./style";
import { AICredentialSelector } from "../../Selectors/AICredentialSelector";
import { useFindActionAuthenticationIds } from "../find-action-authentication-ids";

export const ActionCredentialSelector: React.FC<{
  action: ActionCandidate;
  needToShowError: (hash: string) => boolean;
  onRemove: (id: string) => void;
  setAuthAttempt: (auth: AuthAttempt) => void;
  authenticationMethodDescription?: AuthenticationMethodDescription;
  nextInput?: InputCandidate;
  descriptions?: InputDescription[];
}> = ({
  action,
  needToShowError,
  setAuthAttempt,
  onRemove,
  authenticationMethodDescription,
  nextInput,
  descriptions,
}) => {
  const store = useRootStore();
  const engineClient = useEngineClient();
  const findActionAuthenticationIds = useFindActionAuthenticationIds();

  const { automations } = store;
  const { candidate } = automations;

  const actionGroupType =
    candidate.currentActionGroup?.config.oneofKind ?? "actions";

  if (action.actionReference === undefined) {
    return <></>;
  }
  const { integrationId } = action.actionReference;

  const nextdescription =
    nextInput === undefined
      ? undefined
      : descriptions?.find((d) => d.inputId === nextInput.inputId);

  if (
    authenticationMethodDescription?.type.oneofKind === "dynamicInputs" &&
    authenticationMethodDescription.type.dynamicInputs.inputs.length > 0 &&
    authenticationMethodDescription.type.dynamicInputs.inputs[0].editor?.editor
      .oneofKind === "select" &&
    authenticationMethodDescription.type.dynamicInputs.inputs[0].editor?.editor
      .select.type === SelectEditorDescription_Type.DROPDOWN_WITH_DETAILS
  ) {
    return (
      <StyledInputWrapper
        layout={authenticationMethodDescription?.layout}
        nextInputDescription={
          authenticationMethodDescription?.layout?.position ===
          Position.SAME_LINE
            ? nextdescription
            : undefined
        }
      >
        <AICredentialSelector
          action={action}
          isSubmitted={needToShowError(action.hash)}
          integrationId={integrationId}
          integrations={toJS(store.integrations.list)}
          credentialId={action.credentialId}
          credentials={toJS(store.credentials.list)}
          onChange={(credentialId) => {
            candidate.setActions(
              [A.mergeCredentialId(action.hash, credentialId)],
              actionGroupType,
            );
          }}
          onAdd={(authMethod, initialValues) => {
            const authAttempt: AuthAttempt = {
              integrationId,
              authMethod,
              target: {
                type: "action",
                hash: action.hash,
              },
              initialValues,
            };
            if (
              ["inputs", "paragonAuth", "dynamicInputs"].includes(
                authMethod.type.oneofKind ?? "",
              )
            ) {
              setAuthAttempt(authAttempt);
            } else {
              void authDispatcher(authAttempt, [], store, engineClient);
            }
          }}
          onRemove={onRemove}
          loading={store.automations.isAllDynamicDescriptionsLoaded()}
          isAction={true}
        />
      </StyledInputWrapper>
    );
  }
  return (
    <StyledInputWrapper
      layout={authenticationMethodDescription?.layout}
      nextInputDescription={
        authenticationMethodDescription?.layout?.position === Position.SAME_LINE
          ? nextdescription
          : undefined
      }
    >
      <CredentialSelector
        isSubmitted={needToShowError(action.hash)}
        integrationId={integrationId}
        authenticationMethodIds={findActionAuthenticationIds(action)}
        integrations={toJS(store.integrations.list)}
        credentialId={action.credentialId}
        credentials={toJS(store.credentials.list)}
        onChange={(credentialId) => {
          candidate.setActions(
            [A.mergeCredentialId(action.hash, credentialId)],
            actionGroupType,
          );
        }}
        onAdd={(authMethod) => {
          const authAttempt: AuthAttempt = {
            integrationId,
            authMethod,
            target: {
              type: "action",
              hash: action.hash,
            },
          };
          if (
            ["inputs", "paragonAuth", "dynamicInputs"].includes(
              authMethod.type.oneofKind ?? "",
            )
          ) {
            setAuthAttempt(authAttempt);
          } else {
            void authDispatcher(authAttempt, [], store, engineClient);
          }
        }}
        onRemove={onRemove}
        loading={store.automations.isAllDynamicDescriptionsLoaded()}
        isAction={true}
      />
    </StyledInputWrapper>
  );
};
